'use client'

import type { SegmentChartProps } from '.'

import { motion } from 'framer-motion'
import * as React from 'react'
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as RechartsPrimitive from 'recharts'
import { Bar, BarChart, CartesianGrid, Text, XAxis, YAxis } from 'recharts'

import { useCspNonce } from '@/app/csp-nonce-provider'
import { ChartTooltip } from '@/components/atomic/chart-tooltip'
import Icon, { IconName, PhosphorIconName } from '@/components/atomic/icon'
import {
  BodyMini,
  BodySubtext,
  FontWeight,
  variantStyles,
} from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { useStaggeredAnimation } from '@/hooks/use-staggered-animation'
import { formatPercentage, isNullish } from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

import styles from './segment-chart.module.css'

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const

export type ChartConfig = {
  [k in string]: {
    label?: string
    icon?: React.ComponentType
    color?: string
  }
}

type ChartContextProps = {
  config: ChartConfig
}

const ChartContext = React.createContext<ChartContextProps | null>(null)

function useChart() {
  const context = React.useContext(ChartContext)

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />')
  }

  return context
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >['children']
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn('flex aspect-video justify-center', className)}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer debounce={100}>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
})
ChartContainer.displayName = 'Chart'

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const cspNonce = useCspNonce()
  const colorConfig = Object.entries(config).filter(
    ([_, config]) => config.color,
  )

  if (!colorConfig.length) {
    return null
  }

  return (
    <style nonce={cspNonce}>
      {Object.entries(THEMES)
        .map(
          ([_theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color = itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join('\n')}
}
`,
        )
        .join('\n')}
    </style>
  )
}

const ChartLegend = RechartsPrimitive.Legend

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    Pick<RechartsPrimitive.LegendProps, 'payload' | 'verticalAlign'> & {
      chartData: Record<string, number>
      hideIcon?: boolean
      nameKey?: string
      expanded?: boolean
      onExpandedChange?: (expanded: boolean) => void
      setParentContainerHeight?: Dispatch<SetStateAction<number>>
      width?: number
      baseHeight: number
      enableAnimation?: boolean
    }
>(
  (
    {
      className,
      hideIcon = false,
      payload,
      nameKey,
      chartData,
      expanded = false,
      onExpandedChange,
      setParentContainerHeight,
      width,
      baseHeight,
      enableAnimation = false,
    },
    ref,
  ) => {
    const { config } = useChart()
    const { getAnimationProps } = useStaggeredAnimation()

    // Each time the legend is expanded or collapsed, we need to update the parent container height
    useEffect(() => {
      setParentContainerHeight?.(getDynamicHeight(baseHeight, config, expanded))
    }, [config, expanded, baseHeight, setParentContainerHeight])

    const handleExpandedChange = useCallback(
      (newExpanded: boolean) => {
        onExpandedChange?.(newExpanded)
      },
      [onExpandedChange],
    )

    const sortedPayload = useMemo(() => {
      if (!payload?.length) {
        return []
      }
      return [...payload].sort((itemA, itemB) => {
        const keyA = `${nameKey || itemA.dataKey || 'value'}`
        const keyB = `${nameKey || itemB.dataKey || 'value'}`
        const valueA = chartData[keyA] ?? 0
        const valueB = chartData[keyB] ?? 0
        return valueB - valueA // descending order
      })
    }, [payload, nameKey, chartData])

    const visibleItems = useMemo(() => {
      return sortedPayload.length > MAX_LEGEND_ITEMS && !expanded
        ? sortedPayload.slice(0, MAX_LEGEND_ITEMS - 1)
        : sortedPayload
    }, [sortedPayload, expanded])

    if (!sortedPayload?.length) {
      return null
    }
    return (
      <div
        ref={ref}
        className={cn(
          'absolute grid pt-2',
          sortedPayload.length === 1 ? 'grid-cols-1' : 'grid-cols-2 gap-x-8',
          className,
        )}
        style={{
          // Need to subtract the horizontal offset because of the left padding
          // on the ChartLegend
          width: width ? width - HORIZONTAL_OFFSET : undefined,
        }}
      >
        {visibleItems.map((item, index) => {
          const key = `${nameKey || item.dataKey || 'value'}`
          const itemConfig = getPayloadConfigFromPayload(config, item, key)

          // Only apply animation props if enableAnimation is true
          const animationProps = enableAnimation
            ? getAnimationProps(index)
            : { className: '', style: {} }

          return isNullish(chartData[key]) ? null : (
            <motion.div
              key={item.value}
              className={cn(
                'flex items-center py-1 [&>svg]:h-3 [&>svg]:w-3',
                animationProps.className,
              )}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: index * 0.05,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              style={animationProps.style}
            >
              {/** biome-ignore lint/complexity/noUselessFragments: Bulk suppression as part of biome migration */}
              <>
                {itemConfig?.icon && !hideIcon ? (
                  <itemConfig.icon />
                ) : (
                  <div
                    className="h-2 w-2 shrink-0 rounded-[2px] transition-colors duration-375 ease-in-out"
                    style={{
                      backgroundColor: item.color,
                    }}
                  />
                )}
                <Tooltip simpleText={itemConfig?.label}>
                  <BodySubtext className="text-primary ml-2 truncate">
                    {itemConfig?.label}
                  </BodySubtext>
                </Tooltip>
              </>
              <BodySubtext className="text-primary ml-auto">
                {/* biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess */}
                {formatPercentage(chartData[key]!, {
                  numDigits: 2,
                })}
              </BodySubtext>
            </motion.div>
          )
        })}
        {sortedPayload.length > MAX_LEGEND_ITEMS &&
          (() => {
            // Only apply animation to expand button if enableAnimation is true
            const buttonAnimationProps = enableAnimation
              ? getAnimationProps(visibleItems.length)
              : { className: '', style: {} }

            return (
              // biome-ignore lint/a11y/useButtonType: Bulk suppression as part of biome migration
              <button
                onClick={() => handleExpandedChange(!expanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleExpandedChange(!expanded)
                  }
                }}
                className={cn(
                  'cursor-pointer flex items-center gap-1 py-1 h-[24px] bg-transparent border-0',
                  buttonAnimationProps.className,
                )}
                style={buttonAnimationProps.style}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? 'Show less items'
                    : `View ${sortedPayload.length - (MAX_LEGEND_ITEMS - 1)} more items`
                }
              >
                <Icon
                  iconName={expanded ? IconName.MinusIcon : IconName.PlusIcon}
                />
                <BodySubtext className="text-secondary">
                  {expanded
                    ? 'Show less'
                    : `View ${sortedPayload.length - (MAX_LEGEND_ITEMS - 1)} more`}
                </BodySubtext>
              </button>
            )
          })()}
      </div>
    )
  },
)
ChartLegendContent.displayName = 'ChartLegend'

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined

  let configLabelKey: string = key

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === 'string'
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config]
}

/**
 * Calculates the dynamic height for the chart based on the number of items in the chart configuration.
 *
 * The height is calculated using the formula: `baseHeight + ITEM_PLUS_PADDING_HEIGHT * i`, where `i` is half the number of items
 * in the chart configuration, rounded down. This formula ensures that the chart height increases
 * by ITEM_PLUS_PADDING_HEIGHT pixels for every two items in the configuration, starting from a base height of baseHeight pixels.
 *
 * @param baseHeight - The base height in pixels.
 * @param chartConfig - The configuration object for the chart, where each key represents a chart item.
 * @param expanded - Whether the legend is expanded.
 * @returns The calculated height in pixels.
 */
function getDynamicHeight(
  baseHeight: number,
  chartConfig: ChartConfig,
  expanded: boolean = false,
): number {
  // Filter out keys that have null values in chartConfig
  const keys = Object.keys(chartConfig)
  const validKeys = keys.filter((key) => {
    // Filter out both "null" and "undefined" keys
    return key !== 'null' && key !== 'undefined'
  })

  const totalItems = validKeys.length

  const visibleItems = expanded
    ? // +1 because we need to add the show less button when expanded
      totalItems + 1
    : // If not expanded, only show all the items, _without_ the show more button,
      // if there are less than or equal to MAX_LEGEND_ITEMS items in chartConfig.
      // Otherwise, show MAX_LEGEND_ITEMS items.
      Math.min(totalItems, MAX_LEGEND_ITEMS)
  // Add 1 here because we don't want to add ITEM_PLUS_PADDING_HEIGHT if visibleItems is 0
  // and we want 1 or 2 items to result in an additional ITEM_PLUS_PADDING_HEIGHT px being added
  const i = Math.floor((visibleItems + 1) / 2)
  return baseHeight + ITEM_PLUS_PADDING_HEIGHT * i
}

const SegmentChartNoData = () => {
  return (
    <Text
      style={{ transform: `translate(50%, 50%)` }}
      x={0}
      y={0}
      textAnchor="middle"
      verticalAnchor="middle"
      className={cn(variantStyles.bodySmall)}
    >
      No data available
    </Text>
  )
}

const DEFAULT_WIDTH = 456
const HORIZONTAL_OFFSET = 0
const BAR_SIZE = 24
const HEIGHT_WITHOUT_LEGEND = '24px'
const MAX_LEGEND_ITEMS = 4
const ITEM_PLUS_PADDING_HEIGHT = 24

export function SegmentChart({
  chartData,
  chartConfig,
  width = DEFAULT_WIDTH,
  fullWidth = true,
  showLegend = true,
  targetPercentage,
  differencePercentage,
  withinBounds,
  targetLabel,
  baseHeight,
  setParentContainerHeight,
  preserveOriginalOrder = false,
  enableAnimation = false,
}: Omit<SegmentChartProps, 'title'> & {
  baseHeight: number
  setParentContainerHeight: Dispatch<SetStateAction<number>>
  width?: number
  fullWidth?: boolean
  showLegend?: boolean
  targetPercentage?: number
  differencePercentage?: number
  withinBounds?: boolean
  targetLabel?: string
  preserveOriginalOrder?: boolean
  enableAnimation?: boolean
}) {
  const [isLegendExpanded, setIsLegendExpanded] = useState(false)

  const hasNoData = useMemo(
    () =>
      Object.keys(chartData).length === 0 ||
      Object.values(chartData).every((value) => value === 0),
    [chartData],
  )

  const chartRef = useRef<HTMLDivElement>(null)

  const [chartConfigToPlot, setChartConfigToPlot] = useState(chartConfig)

  const chartDataToPlot = useMemo(() => {
    const filteredAndSortedChartData = Object.fromEntries(
      Object.entries(chartData)
        .filter(([key, value]) => {
          const shouldRetain =
            Object.keys(chartConfig).includes(key) && value > 0
          if (!shouldRetain) {
            setChartConfigToPlot((prev) => {
              const newConfig = { ...prev }
              delete newConfig[key]
              return newConfig
            })
          }
          return shouldRetain
        })
        .sort(([keyA], [keyB]) => {
          // If preserveOriginalOrder is true, don't change the order (return 0)
          // Otherwise, sort by value in descending order
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          return preserveOriginalOrder ? 0 : chartData[keyB]! - chartData[keyA]!
        })
        .map(([oldKey, value], index) => {
          // This is an unfortunate hack that we need to do to ensure that the
          // legend and the bars in the chart are ordered the same way.
          // We cannot simply sort the keys of the object that we would get if we
          // only did the filter and sort above, since Javascript does not respect
          // the order of the keys of an object. Hence, we needed to sort the bars
          // by their value and change their keys to their index in the sorted
          // array.
          // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
          const newKey = index + '-' + oldKey
          const chartConfigEntry = { ...chartConfig[oldKey] }
          setChartConfigToPlot((prev) => {
            const { [oldKey]: _oldValue, ...rest } = prev
            return { ...rest, [newKey]: chartConfigEntry }
          })
          return [newKey, value]
        }),
    )
    return [filteredAndSortedChartData]
  }, [chartData, chartConfig, preserveOriginalOrder])
  // End of data processing

  const [barUnderHover, setBarUnderHover] = useState<string | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 0,
    segmentWidth: 0,
    value: 0,
    first: false,
    last: false,
  })

  return (
    <div className={`${styles['segment-chart-container']} relative mt-2.5`}>
      <ChartContainer
        ref={chartRef}
        config={chartConfigToPlot}
        style={{
          maxHeight: HEIGHT_WITHOUT_LEGEND,
          width: fullWidth ? '100%' : width,
        }}
      >
        <BarChart
          accessibilityLayer
          data={chartDataToPlot}
          layout="vertical"
          barSize={BAR_SIZE}
          margin={{
            right: 0,
            top: -1,
            bottom: -1,
            left: -1 * HORIZONTAL_OFFSET,
          }}
        >
          {hasNoData ? (
            <RechartsPrimitive.Customized component={<SegmentChartNoData />} />
          ) : (
            <>
              <CartesianGrid vertical={false} horizontal={false} />
              <YAxis
                type="category"
                tickLine={false}
                tick={false}
                axisLine={false}
                hide
              />
              {/* XAxis with domain=[0, 100] ensures bars always fill the chart width
               */}
              <XAxis type="number" hide domain={[0, 100]} />

              {/* Optional legend component with expand/collapse functionality */}
              {showLegend && (
                <ChartLegend
                  content={
                    <ChartLegendContent
                      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                      chartData={chartDataToPlot[0]!}
                      expanded={isLegendExpanded}
                      onExpandedChange={setIsLegendExpanded}
                      setParentContainerHeight={setParentContainerHeight}
                      width={width}
                      baseHeight={baseHeight}
                      enableAnimation={enableAnimation}
                    />
                  }
                  // Needed to counteract the negative left margin on the BarChart
                  style={{ marginLeft: HORIZONTAL_OFFSET }}
                />
              )}

              {/* Render each segment as a Bar component */}
              {Object.entries(chartConfigToPlot).map(([key, config]) => {
                // Determine if this segment should be highlighted or dimmed based on hover state
                const isHighlighted =
                  barUnderHover === null || barUnderHover === key

                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="a" // Stack ID ensures bars stack instead of overlapping
                    fill={
                      isHighlighted
                        ? (config as { color: string }).color
                        : 'var(--primary-border)' // Dim non-highlighted segments
                    }
                    isAnimationActive
                    className="transition-colors duration-375 ease-in-out"
                    onMouseEnter={(e) => {
                      setBarUnderHover(key)

                      setTooltipPosition({
                        left: e.x,
                        segmentWidth: e.width,
                        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
                        value: chartDataToPlot[0]![key]!,
                        first: e.value[0] < 15 && e.value[1] < 40,
                        last: e.value[1] > 85 && e.value[0] > 60,
                      })
                    }}
                    onMouseLeave={(_e) => {
                      setBarUnderHover(null)
                    }}
                  />
                )
              })}

              {/* Optional target line to indicate goal/threshold */}
              {targetPercentage !== undefined && targetPercentage > 0 && (
                <RechartsPrimitive.ReferenceLine
                  x={targetPercentage}
                  stroke={'var(--reference-line-stroke)'}
                  strokeDasharray="2 2"
                  strokeWidth={1}
                />
              )}
            </>
          )}
        </BarChart>
      </ChartContainer>

      {/* Fixed position tooltip that appears above the segment */}
      {barUnderHover && !hasNoData && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            top: -43, // Position above the chart
          }}
          ref={(element) => {
            if (element) {
              const tooltipWidth = element.getBoundingClientRect().width
              const segmentWidth = tooltipPosition.segmentWidth

              if (tooltipPosition.first) {
                element.style.left = `0px`
                element.style.right = `auto`
              } else if (tooltipPosition.last) {
                element.style.right = `0px`
                element.style.left = `auto`
              } else {
                const left = ((tooltipWidth - segmentWidth) / 2) * -1 // Center the tooltip

                element.style.left = `${tooltipPosition.left + left}px`
                element.style.right = `auto`
              }
            }
          }}
        >
          <ChartTooltip
            values={[
              {
                label: chartConfigToPlot[barUnderHover]?.label,
                value: formatPercentage(tooltipPosition.value, {
                  numDigits: 2,
                }),
                color: chartConfigToPlot[barUnderHover]?.color,
              },
            ]}
          />
        </div>
      )}

      {!hasNoData &&
        targetPercentage !== undefined &&
        targetPercentage > 0 &&
        differencePercentage !== undefined &&
        differencePercentage !== 0 && (
          <div
            style={{
              position: 'sticky',
              transform: `translateY(${-46}px)`,
              width: 'fit-content',
            }}
          >
            <div className="flex items-center gap-1 p-[2px] pr-1 rounded-sm bg-moment-orange-200 dark:bg-moment-orange-1000">
              <Icon
                iconName={PhosphorIconName.TargetIcon}
                size={'small'}
                isPhosphor
                className="text-moment-orange-700 dark:text-moment-orange-500"
              />
              {!withinBounds && (
                <BodyMini
                  fontWeight={FontWeight.Medium}
                  className="text-moment-orange-700 dark:text-moment-orange-500"
                >
                  {`${formatPercentage(Math.abs(differencePercentage), {
                    numDigits: 2,
                  })} ${differencePercentage ? `${differencePercentage > 0 ? 'over' : 'under'}` : ''} ${targetLabel}`}
                </BodyMini>
              )}
            </div>
          </div>
        )}
    </div>
  )
}
